/**
 * The one entry point callers should use to price a booking.
 *
 * Routes to the legacy or decimal engine by the booking's `created_at`, and
 * always returns cents, so no call site has to know which engine ran or
 * remember to convert.
 *
 * `created_at` is required, deliberately. Every legacy pricing function
 * defaults it to `new Date()`, which means an edit to a 2025 booking reprices
 * it on today's rate card and charges the customer the difference for a change
 * that never touched the price. That default is the single most dangerous line
 * in the old pricing code and it is not reproduced here.
 */

import { CleaningTypeId, getRateCard } from "./rates";
import { resolveCleaningTypeOrThrow } from "./cleaning-types";
import { Cents, dollarsToCents, percentOfCents } from "./money";
import { PriceBreakdown, getBookingPrice, getTaxCents } from "./decimal";
import { LegacyBreakdown, getLegacyBreakdown } from "./legacy";
import {
  DAYS_OF_WEEK,
  PricingConfig,
  ProximityTier,
  dayOfWeekForServiceDate,
  defaultPricingConfig,
} from "./config";

export interface QuoteInput {
  bedrooms: string | number;
  bathrooms: string | number;
  /** A cleaning type id, a legacy name, or the legacy price string. */
  cleaning_type: string | number;
  clean_time: string;
  frequency: string;
  add_ons?: string[] | null;
  created_at: Date;
  /** The service date (`clean_date`), which decides the surge day of week. */
  service_date?: string;
  /** Whether the customer took Same-Day. */
  same_day?: boolean;
  /** The proximity tier the server resolved. Defaults to "none". */
  proximity_tier?: ProximityTier;
}

export interface Quote {
  engine: "legacy" | "decimal";
  rateCardVersion: string;
  cleaningType: CleaningTypeId;
  totalCents: Cents;
  /**
   * The lines that produced `totalCents`, on whichever engine produced it.
   *
   * This used to be decimal-only, on the grounds that the legacy engine
   * quantises mid-chain and so a breakdown for it "would be a lie". That was
   * true of a breakdown *recomputed* on the cents engine, which is what every
   * caller ended up doing — and it is why the admin panel showed a $169.20
   * subtotal and $15.02 of tax above a charged total of $184.00. It is not true
   * of the legacy engine's own intermediate values: `getLegacyBreakdown`
   * returns those, and `legacy.test.ts` pins that they sum to the total across
   * the input matrix. Both engines now report lines that reconcile.
   */
  breakdown: PriceBreakdown;
}

/**
 * Widen a whole-dollar legacy breakdown to the cents shape the decimal engine
 * reports, so a caller can render either without branching. Every legacy field
 * is a whole number of dollars, so each conversion is exact.
 */
function toPriceBreakdown(legacy: LegacyBreakdown): PriceBreakdown {
  return {
    baseCents: dollarsToCents(legacy.base),
    roomsCents: dollarsToCents(legacy.rooms),
    cleaningTypeCents: dollarsToCents(legacy.cleaningType),
    /**
     * Zero here, and filled in afterwards by `withLegacyDayOfWeekSurge` when the
     * booking's service date carries surge. The legacy engine itself has no
     * concept of it — this widens its output, it does not reach inside it.
     */
    dayOfWeekSurgeCents: 0,
    // Always zero. Same-Day is refused outright on a legacy card: it needs an
    // arrival window and a per-cleaning-type surcharge grid that a pre-V5
    // booking never had.
    sameDaySurchargeCents: 0,
    // Always zero, and refused outright for the same reason: a pre-decimal
    // booking predates proximity, and the ZIP it would have been resolved
    // against was never recorded on it.
    travelPremiumCents: 0,
    proximityDiscountCents: 0,
    /**
     * Always zero on a legacy card. Unlike the day-of-week surge below -- which
     * a reschedule legitimately picks up, because a booking moved to a Friday is
     * a Friday booking -- the legacy engine quantises its subtotal to whole
     * dollars, and a percentage of base taken off that would not reconcile with
     * what the customer was charged. A pre-V5 booking predates the axis, so
     * zero is also what it was sold at.
     */
    timeOfDayPremiumCents: 0,
    timeOfDayDiscountCents: 0,
    frequencyDiscountCents: dollarsToCents(legacy.frequencyDiscount),
    flexibleDiscountCents: dollarsToCents(legacy.flexibleDiscount),
    promoDiscountCents: dollarsToCents(legacy.promoDiscount),
    addOnsCents: dollarsToCents(legacy.addOns),
    subTotalCents: dollarsToCents(legacy.subTotal),
    taxCents: dollarsToCents(legacy.tax),
    totalCents: dollarsToCents(legacy.total),
  };
}

/**
 * Add day-of-week surge to a legacy breakdown, without touching what the legacy
 * engine computed.
 *
 * The surge is added *beside* the legacy numbers rather than folded into them.
 * `legacy.ts` must keep producing exactly the figures it produces today — it
 * quantises the subtotal to whole dollars and rounds tax to the nearest dollar,
 * and those quirks are what a sold booking was charged. Re-running that
 * arithmetic over a surged subtotal would reprice the base as a side effect of
 * a reschedule.
 *
 * So the surge is a percentage of the legacy base, taxed to the cent, and both
 * are added on top. A booking on a day with no surge gets back the object it
 * came in with, byte for byte. Surge stacking additively on base is ADR 0005;
 * this is the same shape the decimal engine uses.
 */
function withLegacyDayOfWeekSurge(
  breakdown: PriceBreakdown,
  surgePercent: number,
  label: string
): PriceBreakdown {
  if (surgePercent === 0) return breakdown;

  const dayOfWeekSurgeCents = percentOfCents(
    breakdown.baseCents,
    surgePercent,
    label
  );
  const surgeTaxCents = getTaxCents(dayOfWeekSurgeCents);

  return {
    ...breakdown,
    dayOfWeekSurgeCents,
    subTotalCents: breakdown.subTotalCents + dayOfWeekSurgeCents,
    taxCents: breakdown.taxCents + surgeTaxCents,
    totalCents: breakdown.totalCents + dayOfWeekSurgeCents + surgeTaxCents,
  };
}

/**
 * The day-of-week surge a legacy booking's service date carries.
 *
 * Throws rather than defaulting to zero when the config surges some day and the
 * caller did not say which day this cleaning falls on. Silently returning zero
 * there would undercharge a rescheduled booking and look like the feature
 * simply not applying, which is indistinguishable from working correctly.
 */
function legacySurgePercent(
  serviceDate: string | undefined,
  config: PricingConfig
): { percent: number; label: string } {
  if (serviceDate) {
    const day = dayOfWeekForServiceDate(serviceDate);
    return { percent: config.dayOfWeekSurgePercent[day], label: `${day} surge` };
  }

  const surgesSomeDay = DAYS_OF_WEEK.some(
    (day) => config.dayOfWeekSurgePercent[day] !== 0
  );
  if (surgesSomeDay) {
    throw new RangeError(
      "service_date is required to price this booking — day-of-week surge is " +
        "set on this config, so the price depends on which day the cleaning " +
        "happens. Defaulting to a day would charge one day's rate for another's."
    );
  }
  return { percent: 0, label: "surge" };
}

function isNumericWireValue(value: string | number): boolean {
  const raw = String(value).trim().replace(/[$,]/g, "");
  return raw !== "" && Number.isFinite(Number(raw));
}

export function quoteBooking(
  input: QuoteInput,
  promoDiscountCents: Cents = 0,
  /**
   * The rates in force. Defaults to the hardcoded card for `created_at` with no
   * surge, which is what every pre-ADR-0003 caller was already getting.
   */
  config: PricingConfig = defaultPricingConfig(input.created_at)
): Quote {
  if (!input.created_at) {
    throw new RangeError(
      "created_at is required to price a booking — omitting it would price an " +
        "existing booking on today's rate card instead of the one it was sold under."
    );
  }

  const card = config.rateCard;
  const cleaningType = resolveCleaningTypeOrThrow(input.cleaning_type, input.created_at);

  if (!card.supportsDecimals) {
    /**
     * Same-Day is refused on a legacy card; day-of-week surge is not.
     *
     * This guard used to reject *any* surge-bearing config here, on the reading
     * that a pre-V5 booking predates the whole surge feature. That conflated two
     * instants which `resolveSurgeInstant` deliberately keeps apart. The rate
     * card is pinned to `created_at` so a reschedule cannot reprice the base —
     * that is what the guard was protecting. The surge instant moves *on
     * purpose* when the service date changes, because a booking moved from a
     * Tuesday to a Friday is a Friday booking and should carry Friday's surge.
     *
     * The old rule made those two facts contradict each other, and the
     * contradiction was total rather than academic: every booking created before
     * the 2026-08-02 cutover threw on any date change, so admins could not
     * reschedule an old booking at all.
     *
     * Same-Day still throws. It is not a rate that a date change can pick up: it
     * needs an arrival window and a per-cleaning-type surcharge grid, and a
     * legacy booking that claims it is a booking whose config was misloaded.
     */
    if (input.same_day) {
      throw new RangeError(
        `Same-Day does not apply to bookings priced on the legacy engine (rate ` +
          `card ${card.version}, created_at ${input.created_at.toISOString()}). ` +
          `A pre-decimal booking predates Same-Day, so one marked Same-Day means ` +
          `the wrong config was loaded for it.`
      );
    }
    /**
     * Proximity, refused on the same footing.
     *
     * A pre-V5 booking predates the ZIP column as well as the feature, so there
     * is nothing to have resolved a tier against. Unlike the day-of-week surge
     * below — which a reschedule legitimately picks up, because a booking moved
     * to a Friday is a Friday booking — a tier on a legacy booking is not a
     * value a date change can acquire. It means the wrong config was loaded.
     */
    if (input.proximity_tier && input.proximity_tier !== "none") {
      throw new RangeError(
        `Proximity pricing does not apply to bookings priced on the legacy ` +
          `engine (rate card ${card.version}, created_at ` +
          `${input.created_at.toISOString()}). A pre-decimal booking has no ZIP ` +
          `recorded to have resolved a tier against.`
      );
    }
    // Preserve the stored wire value when it is a price: that is what the
    // legacy engine saw when the booking was sold, and reproducing it exactly
    // is the point. Only derive from the card when the caller sent an id.
    const legacyPrice = isNumericWireValue(input.cleaning_type)
      ? String(input.cleaning_type)
      : String(card.cleaningTypes[cleaningType] / 100);

    const legacy = getLegacyBreakdown(
      {
        bedrooms: input.bedrooms,
        bathrooms: input.bathrooms,
        cleaning_type: legacyPrice,
        clean_time: input.clean_time,
        frequency: input.frequency,
        add_ons: input.add_ons,
        created_at: input.created_at,
      },
      promoDiscountCents / 100
    );

    const { percent, label } = legacySurgePercent(input.service_date, config);
    const breakdown = withLegacyDayOfWeekSurge(
      toPriceBreakdown(legacy),
      percent,
      label
    );

    return {
      engine: "legacy",
      rateCardVersion: card.version,
      cleaningType,
      totalCents: breakdown.totalCents,
      breakdown,
    };
  }

  const breakdown = getBookingPrice(
    {
      bedrooms: input.bedrooms,
      bathrooms: input.bathrooms,
      cleaningType,
      clean_time: input.clean_time,
      frequency: input.frequency,
      add_ons: input.add_ons,
      created_at: input.created_at,
      service_date: input.service_date,
      same_day: input.same_day,
      proximity_tier: input.proximity_tier,
    },
    promoDiscountCents,
    config
  );

  return {
    engine: "decimal",
    rateCardVersion: card.version,
    cleaningType,
    totalCents: breakdown.totalCents,
    breakdown,
  };
}

/** Whether a booking created at this instant prices on the decimal engine. */
export function usesDecimalPricing(createdAt: Date): boolean {
  return getRateCard(createdAt).supportsDecimals;
}
